import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import signalRService from '../services/SignalRService';
import { IdentificationId } from '../services/util';
import { useAuth } from '../hooks/auth';

const SignalRContext = createContext(null);

export const SignalRProvider = ({ children }) => {
  const { identificationId } = useAuth();
  const identification = IdentificationId();
  // React state takes priority, with fallback to local storage
  const activeIdentificationId =
    identificationId || identification.identificationId;

  const [isConnected, setIsConnected] = useState(false);
  const [joinedConversationId, setJoinedConversationId] = useState(null);

  useEffect(() => {
    // If not logged in, do nothing (SignalR remains stopped)
    if (!activeIdentificationId) {
      setIsConnected(false);
      setJoinedConversationId(null);
      return;
    }

    console.log(
      '[SignalRContext] Starting SignalR with ID:',
      activeIdentificationId
    );
    let mounted = true;

    const unsubConn = signalRService.subscribeConnection((status) => {
      if (mounted) setIsConnected(status);
    });

    const unsubJoin = signalRService.subscribeJoin((conversationId) => {
      if (mounted) setJoinedConversationId(conversationId);
    });

    // Sync current status immediately
    if (signalRService.isConnected) {
      setIsConnected(true);
    }

    signalRService.start(activeIdentificationId).catch((err) => {
      console.error('[SignalRContext] Start failed:', err);
    });

    // Cleanup only unsubscribes — NEVER stop connection on re-render
    return () => {
      mounted = false;
      unsubConn();
      unsubJoin();
    };
  }, [activeIdentificationId]);

  const joinConversation = useCallback(
    (conversationId) => signalRService.joinConversation(conversationId),
    []
  );

  const leaveConversation = useCallback(
    (conversationId) => signalRService.leaveConversation(conversationId),
    []
  );

  const reconnectSignalR = useCallback(() => {
    return signalRService.forceReconnect();
  }, []);

  const subscribeReceiveMessage = useCallback(
    (cb) => signalRService.subscribeReceiveMessage(cb),
    []
  );

  const subscribeConversationSummary = useCallback(
    (cb) => signalRService.subscribeConversationSummary(cb),
    []
  );

  const subscribeReaction = useCallback(
    (cb) => signalRService.subscribeReaction(cb),
    []
  );

  const subscribeIncomingCall = useCallback(
    (cb) => signalRService.subscribeIncomingCall(cb),
    []
  );

  const subscribeMessageDelivered = useCallback(
    (cb) => signalRService.subscribeMessageDelivered(cb),
    []
  );

  const subscribeMessageRead = useCallback(
    (cb) => signalRService.subscribeMessageRead(cb),
    []
  );

  // Send Direct Message via SignalR
  const sendMessageUsingContactInformation_SignalR = useCallback(
    ({
      contactInformation,
      Message,
      conversationGuid,
      Attachments,
      IsHtmlMessage,
    }) => {
      const chatRequest = {
        identificationId: identification.identificationId,
        ApplicationType: identification.appType,
        platformType: identification.platformType,
        versionNumber: identification.versionNumber,
        IsGroup: false,
        ReceivedGuid: contactInformation?.StaffGuid || contactInformation,
        ContactInformation: contactInformation,
        StaffMessage: {
          Attachments: Attachments || [],
          Message,
          ParentMessageGuid: null,
          MessageDateTime: `/Date(${Date.now()})/`,
          IsPoll: false,
          IsHtmlMessage,
        },
      };

      console.log('[SignalR] Sending 1-on-1 Message chatRequest:', chatRequest);

      return signalRService.sendMessageUsingContactInformation(
        conversationGuid,
        chatRequest
      );
    },
    [identification]
  );

  // Send Group Message via SignalR
  const sendMessageUsingFilter_SignalR = useCallback(
    ({
      contactInformation,
      Message,
      conversationGuid,
      Attachments,
      IsHtmlMessage,
    }) => {
      const groupGuid = contactInformation?.StaffGuid || contactInformation;

      const chatGroupRequest = {
        identificationId: identification.identificationId,
        ApplicationType: identification.appType,
        platformType: identification.platformType,
        versionNumber: identification.versionNumber,
        IsGroup: true,
        BroadcastMessage: true,
        ReceivedGuid: groupGuid,
        ContactInformationFilter: {
          IncludedStaffGroupGuids: [groupGuid],
          IncludedOnlineStates: [1, 2, 4, 8],
        },
        StaffMessage: {
          Attachments: Attachments || [],
          Message,
          ParentMessageGuid: null,
          MessageDateTime: `/Date(${Date.now()})/`,
          IsPoll: false,
          IsHtmlMessage,
        },
      };

      console.log(
        '[SignalR] Sending Group Message chatGroupRequest:',
        chatGroupRequest
      );

      return signalRService.sendMessageUsingFilter(
        conversationGuid,
        chatGroupRequest
      );
    },
    [identification]
  );

  // Direct invocation matching SignalRService
  const sendReactionMessage = useCallback(
    (conversationGuidId, reactionDataRequest) => {
      return signalRService.sendReactionMessage(
        conversationGuidId,
        reactionDataRequest
      );
    },
    []
  );

  const markAllMessageReceived_SignalR = useCallback(
    ({ conversationGuid }) => {
      const guid =
        typeof conversationGuid === 'object'
          ? conversationGuid?.Guid
          : conversationGuid;
      if (!guid) return;

      const request = {
        IdentificationId:
          activeIdentificationId || identification.identificationId,
        ApplicationType: identification.appType || 4000,
        PlatformType: identification.platformType || 7,
      };

      return signalRService.markAllMessageReceived(guid, request);
    },
    [activeIdentificationId, identification]
  );

  const markAllMessageRead_SignalR = useCallback(
    ({ conversationGuid }) => {
      const guid =
        typeof conversationGuid === 'object'
          ? conversationGuid?.Guid
          : conversationGuid;
      if (!guid) return;

      const request = {
        IdentificationId:
          activeIdentificationId || identification.identificationId,
        ApplicationType: identification.appType || 4000,
        PlatformType: identification.platformType || 7,
      };

      return signalRService.markAllMessageRead(guid, request);
    },
    [activeIdentificationId, identification]
  );
  // ---------------------------
  // Send Reaction Message (Contact Info)
  // ---------------------------
  const sendReactionData_SignalR = useCallback(
    ({
      conversationGuid,
      isGroup,
      ReceivedGuid,
      StaffMessage,
      MessageOwnerId,
    }) => {
      const reactionDataRequest = {
        identificationId: MessageOwnerId,
        ApplicationType: identification.appType,
        platformType: identification.platformType,
        versionNumber: identification.versionNumber,
        ReceivedGuid: ReceivedGuid,
        IsGroup: isGroup,
        StaffMessage,
      };

      return signalRService.sendReactionMessage(
        conversationGuid,
        reactionDataRequest
      );
    },
    [identification]
  );

  // ---------------------------
  // Send Reaction Message (Contact Info)
  // ---------------------------
  const sendReactionDataUsingGroup_SignalR = useCallback(
    ({
      conversationGuid,
      isGroup,
      ReceivedGuid,
      StaffMessage,
      MessageOwnerId,
    }) => {
      const reactionDataUsingGroupRequest = {
        identificationId: MessageOwnerId,
        ApplicationType: identification.appType,
        platformType: identification.platformType,
        versionNumber: identification.versionNumber,
        ReceivedGuid: ReceivedGuid,
        IsGroup: isGroup,
        StaffMessage,
      };

      return signalRService.sendReactionMessage(
        conversationGuid,
        reactionDataUsingGroupRequest
      );
    },
    [identification]
  );

  // Always reflect the live service status or React state
  const isSignalRConnected = isConnected || signalRService.isConnected;

  return (
    <SignalRContext.Provider
      value={{
        isConnected: isSignalRConnected,
        joinedConversationId,
        joinConversation,
        leaveConversation,
        reconnectSignalR,
        sendMessageUsingContactInformation_SignalR,
        sendMessageUsingFilter_SignalR,
        sendReactionMessage,
        markAllMessageReceived_SignalR,
        markAllMessageRead_SignalR,
        subscribeReceiveMessage,
        subscribeConversationSummary,
        subscribeReaction,
        subscribeIncomingCall,
        subscribeMessageDelivered,
        subscribeMessageRead,
        sendReactionDataUsingGroup_SignalR,
        sendReactionData_SignalR,
        signalRService,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalRContext = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalRContext must be used within a SignalRProvider');
  }
  return context;
};
